import React from 'react';
import { DatePicker, Space } from 'antd';
import { useApolloClient } from '@apollo/client';
import { Col, Form, FormGroup, Input, Row } from 'reactstrap';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import moment from 'moment';
import { successMsg } from '../../../cache/vars';
import { DELETE_FIELD_FROM_CARD } from '../../../queries/vip-workflow';
import { renderFieldChanges } from '../../Popovers/FieldChangesPopover';
import { PIT_PASS_TYPES, transformTicketMessageByType, VIP_CLUB_TYPES } from '../../../utils/constants';



const camelCase = (str: string) => {
  return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
};

const VALIDATION_SCHEMA = Yup.object().shape({
  cardObjectKey: Yup.string().max(40).min(1).matches(/^\S/).required('Required'),
});

const FullyEditableFormSection = ({
  modalState,
  setModalState,
  setPackageOrderWithDetails,
  cartJsonDataLogs,
  ticketTypes,
  packageOrderWithDetails,
}: any) => {
  const client = useApolloClient();

  const [isElementOnDrag, setIsElementOnDrag] = React.useState(false);
  const [ticketsSent, setTicketsSent] = React.useState(false);
  const [ticketsSentDate, setTicketsSentDate] = React.useState('');

  const handleOnDragEnd = async (result: any) => {
    setIsElementOnDrag(false);

    if (!result.destination) return;

    const items = Array.from(modalState.cartData);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setModalState((prev: any) => {
      return {
        ...prev,
        cartData: items,
      };
    });
  };

  const handleTicketsSentChange = (e: { target: { checked: any } }) => {
    const checked = e.target.checked;
    setTicketsSent(checked);
    let newDate = ticketsSentDate;
    if ((checked && !newDate) || (checked && newDate === '-')) {
      newDate = moment(new Date()).format('MM/DD/YYYY');
      setTicketsSentDate(newDate);
    } else if (!checked) {
      newDate = '';
      setTicketsSentDate('');
    }

    updateModalStateForTicketsSent(checked, newDate);
  };

  const handleTicketsSentDateChange = (date: moment.Moment | null) => {
    const newDate = date ? date.format('MM/DD/YYYY') : '';
    setTicketsSentDate(newDate);
    updateModalStateForTicketsSent(ticketsSent, newDate);
  };

  const updateModalStateForTicketsSent = (ticketsSent: boolean, ticketsSentDate: string) => {
    setModalState((prev: any) => {
      return {
        ...prev,
        cartData: prev.cartData.map((fieldData: any) => {
          if ('ticketsSent' in fieldData) {
            return { 
              ...fieldData, 
              ticketsSent: ticketsSent,
            };
          }
          if ('ticketsSentDate' in fieldData) {
            return { ...fieldData, ticketsSentDate: ticketsSentDate };
          }

          return fieldData;
        }),
      };
    });
  };

  const updateModalStateForTicketType = (e: any) => {
    if (!e.target.value) return;
    
    const ticketType = e.target.value;

    const notes = transformTicketMessageByType(ticketType,ticketTypes);

    setModalState((prev: any) => {
      return {
        ...prev,
        cartData: prev.cartData.map((fieldData: any) => {
          if ('ticketType' in fieldData) {
            return { 
              ...fieldData, 
              ticketType: Number(ticketType) || null,
            };
          }

          if ('notes' in fieldData) {
            return { ...fieldData, notes: notes };
          }
          return fieldData;
        }),
      };
    });
  };

  React.useEffect(() => {
    if (modalState.cartData && Array.isArray(modalState.cartData)) {
      const ticketsSentField = modalState.cartData.find((field: any) => 'ticketsSent' in field);
      const ticketsSentDateField = modalState.cartData.find(
        (field: any) => 'ticketsSentDate' in field,
      );
      if (ticketsSentField && ticketsSentDateField) {
        setTicketsSent(ticketsSentField.ticketsSent);
        setTicketsSentDate(ticketsSentDateField.ticketsSentDate);
      }
    }
  }, [modalState.cartData]);

  return (
    <div className="mb-4">
      <Formik
        initialValues={{ cardObjectKey: '' }}
        onSubmit={(keyData, { resetForm }) => {
          setModalState((prev: any) => {
            return {
              ...prev,
              cartData: [...prev.cartData, { [`${keyData.cardObjectKey}`]: '' }],
            };
          });

          resetForm();
        }}
        validationSchema={VALIDATION_SCHEMA}
      >
        {(props) => {
          const { values, errors, handleChange, handleSubmit } = props;
          return (
            <Form>
              <Row className="pb-4 border-bottom">
                <Col sm="8" className="d-flex pl-4 pr-4">
                  <Input
                    type="text"
                    className="mr-3"
                    placeholder="Field Name (max 40)"
                    onChange={handleChange}
                    name="cardObjectKey"
                    value={values.cardObjectKey}
                  />
                  <button
                    disabled={!!errors.cardObjectKey || !values.cardObjectKey}
                    className="btn btn-primary ml-3"
                    type="submit"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubmit();
                    }}
                  >
                    Add
                  </button>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
      <DragDropContext
        onDragEnd={handleOnDragEnd}
        onBeforeDragStart={() => {
          setIsElementOnDrag(true);
        }}
      >
        <Droppable droppableId="input">
          {(providedDroppable) => (
            <div {...providedDroppable.droppableProps} ref={providedDroppable.innerRef}>
              <Row className="pt-4">
                {modalState?.cartData?.map((field: any, id: number) => {
                  return Object.entries(field).map(([fieldKey, fieldValue]: any) => {
                    return (
                      <Col className="pl-4" xs="12" key={id}>
                        <Draggable index={id} draggableId={id.toString()} key={id}>
                          {(providedDraggable) => (
                            <div
                              {...providedDraggable.dragHandleProps}
                              {...providedDraggable.draggableProps}
                              ref={providedDraggable.innerRef}
                            >
                              {fieldKey === 'ticketsSent' && (
                                <FormGroup key={id} className="mr-3">
                                  <div className="d-flex justify-content-between">
                                    <div>
                                      <label className="form-control-label text-capitalize">
                                        {fieldKey}
                                      </label>
                                      {(modalState?.cartId || modalState?.isPublished) && renderFieldChanges(
                                        {config: {name: fieldKey, isShowChanges: true}, logs: cartJsonDataLogs, index: id, packageDetailsId: modalState.packageDetailsId}
                                      )}
                                    </div>

                                    <Input
                                      className="form-control-alternative new-event--title ml-9"
                                      type="checkbox"
                                      name={fieldKey}
                                      onChange={handleTicketsSentChange}
                                      checked={fieldValue || false}
                                    />
                                  </div>
                                </FormGroup>
                              )}
                              {ticketsSent && fieldKey === 'ticketsSentDate' && (
                                <FormGroup key={id} className="mr-3">
                                  <div className="d-flex">
                                    <label className="form-control-label text-capitalize">
                                      {fieldKey}
                                    </label>
                                    <div>
                                      {(modalState?.cartId || modalState?.isPublished) && renderFieldChanges(
                                        {config: {name: fieldKey, isShowChanges: true}, logs: cartJsonDataLogs, index: id, packageDetailsId: modalState.packageDetailsId}
                                      )}
                                    </div>
                                  </div>
                                  <Space direction="vertical" className="d-flex w-full">
                                    <DatePicker
                                      className="d-flex w-25 form-control-alternative border-0 new-event--title  form-control"
                                      allowClear={false}
                                      placeholder={field.placeholder || 'Select Date'}
                                      name={fieldKey}
                                      onChange={(date) => handleTicketsSentDateChange(date)}
                                      format={field.defaultFormat || "MM/DD/YYYY"}
                                      value={ticketsSentDate ? moment(ticketsSentDate, 'MM/DD/YYYY') : null}
                                    />
                                  </Space>
                                </FormGroup>
                              )}
                              {fieldKey === 'ticketType' && (
                                <Col xs="6" key={id} className="p-0">
                                  <FormGroup className="mr-3">
                                    <div className="d-flex">
                                      <label className="form-control-label text-capitalize">
                                        {fieldKey}
                                      </label>
                                      <div>
                                        {(modalState?.cartId || modalState?.isPublished) && renderFieldChanges(
                                          {config: {name: fieldKey, isShowChanges: true}, logs: cartJsonDataLogs, index: id, packageDetailsId: modalState.packageDetailsId}
                                        )}
                                      </div>
                                    </div>
                                    <Space direction="vertical" className="d-flex" style={{ width: '270px' }}>
                                      <Input
                                        className="d-flex w-full form-control-alternative border-0 new-event--title  form-control"
                                        type="select"
                                        name={fieldKey}
                                        value={fieldValue ? Number(fieldValue) : ''}
                                        onChange={updateModalStateForTicketType}
                                      >
                                        <option value={''} hidden>Select ticket type</option>
                                        {modalState.typeId === 321 &&
                                          ticketTypes?.filter((option: any) => option.deliveryType === VIP_CLUB_TYPES[option.id as keyof typeof VIP_CLUB_TYPES]).map((option: Record<string, any>) => {
                                            return <option key={option.id} value={option.id}>{option.deliveryType}</option>;
                                        })
                                        }
                                        {modalState.typeId === 183 &&
                                          ticketTypes?.filter((option: any) => option.deliveryType === PIT_PASS_TYPES[option.id as keyof typeof PIT_PASS_TYPES]).map((option: Record<string, any>) => {
                                            return <option key={option.id} value={option.id}>{option.deliveryType}</option>;
                                          })
                                        }
                                      </Input>
                                    </Space>
                                  </FormGroup>
                                </Col>
                              )}
                              {fieldKey === 'notes' && (
                                <FormGroup key={id} className="mr-3">
                                  <div className="d-flex">
                                    <label className="form-control-label text-capitalize">
                                      Additional notes
                                    </label>
                                    <div>
                                      {(modalState?.cartId || modalState?.isPublished) && renderFieldChanges(
                                        {config: {name: fieldKey, isShowChanges: true}, logs: cartJsonDataLogs, index: id, packageDetailsId: modalState.packageDetailsId}
                                      )}
                                    </div>
                                  </div>
                                  <Input
                                    className="form-control-alternative new-event--title"
                                    type="textarea"
                                    style={{ minHeight: '120px' }}
                                    name={fieldKey}
                                    placeholder="Enter Value"
                                    // onChange={updateModalStateForTicketType}
                                    onChange={
                                      (e) => {
                                        setModalState((prev: any) => {
                                          return {
                                            ...prev,
                                            cartData: [
                                              ...prev.cartData.map((fieldData: any, index: number) =>
                                                index === id
                                                  ? { [fieldKey]: e.target.value }
                                                  : fieldData,
                                              ),
                                            ],
                                          };
                                        });
                                      }
                                    }
                                    value={fieldValue}
                                  />
                                </FormGroup>
                              )}
                              {fieldKey === 'description' && (
                                <FormGroup key={id} className="mr-3">
                                  <div className="d-flex">
                                    <label className="form-control-label text-capitalize">
                                      {fieldKey}
                                    </label>
                                    <div>
                                      {(modalState?.cartId || modalState?.isPublished) && renderFieldChanges(
                                        {config: {name: fieldKey, isShowChanges: true}, logs: cartJsonDataLogs, index: id, packageDetailsId: modalState.packageDetailsId}
                                      )}
                                    </div>
                                  </div>
                                  <Input
                                    className="form-control-alternative new-event--title"
                                    type="textarea"
                                    style={{ minHeight: '120px' }}
                                    name={fieldKey}
                                    placeholder="Enter Value"
                                    onChange={
                                      (e) => {
                                        setModalState((prev: any) => {
                                          return {
                                            ...prev,
                                            cartData: [
                                              ...prev.cartData.map((fieldData: any, index: number) =>
                                                index === id
                                                  ? { [fieldKey]: e.target.value }
                                                  : fieldData,
                                              ),
                                            ],
                                          };
                                        });
                                      }
                                    }
                                    value={fieldValue}
                                  />
                                </FormGroup>
                              )}
                              {fieldKey !== 'ticketsSent' && fieldKey !== 'ticketsSentDate' && fieldKey !== 'ticketType' && fieldKey !== 'description' && fieldKey !== 'notes' && (
                                <FormGroup key={id} className="mr-3">
                                  <div className="d-flex justify-content-between">
                                    <div>
                                      <label className="form-control-label text-capitalize">
                                        {fieldKey}
                                      </label>
                                      {(modalState?.cartId || modalState?.isPublished) && renderFieldChanges(
                                        {config: {name: fieldKey, isShowChanges: true}, logs: cartJsonDataLogs, index: id, packageDetailsId: modalState.packageDetailsId}
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      className="text-primary revert-btn"
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        if (!modalState.cartId) {
                                          setModalState((prev: any) => {
                                            prev.cartData.splice(id, 1);

                                            return {
                                              ...prev,
                                              cartData: prev.cartData,
                                            };
                                          });
                                          successMsg('Field Deleted');
                                        } else {
                                          const { data } = await client.mutate({
                                            mutation: DELETE_FIELD_FROM_CARD,
                                            variables: {
                                              cartId: modalState.cartId,
                                              fieldName: fieldKey,
                                              fieldId: id,
                                            },
                                          });
                                          if (data?.deleteFieldFromCard === 'ok') {
                                            setModalState((prev: any) => {
                                              prev.cartData.splice(id, 1);

                                              return {
                                                ...prev,
                                                cartData: prev.cartData,
                                              };
                                            });
                                            setPackageOrderWithDetails((prev: any) => {
                                              return {
                                                ...prev,
                                                orderDetails: prev.orderDetails.map((item: any) =>
                                                  item.packageDetailsId ===
                                                  modalState.packageDetailsId
                                                    ? {
                                                        ...item,
                                                        cartDetails: {
                                                          ...item.cartDetails,
                                                          cartJsonData: JSON.stringify(
                                                            modalState.cartData,
                                                          ),
                                                        },
                                                      }
                                                    : item,
                                                ),
                                              };
                                            });
                                            successMsg('Field Deleted');
                                          }
                                        }
                                      }}
                                    >
                                      Delete field
                                    </button>
                                  </div>
                                  <Input
                                    className="form-control-alternative new-event--title"
                                    type="text"
                                    name={fieldKey}
                                    placeholder={fieldKey.toLowerCase() === 'link' ? 'wrapper(https://example.com)' : 'Enter Value'}
                                    onChange={(e) => {
                                      setModalState((prev: any) => {
                                        return {
                                          ...prev,
                                          cartData: [
                                            ...prev.cartData.map((fieldData: any, index: number) =>
                                              index === id
                                                ? { [fieldKey]: e.target.value }
                                                : fieldData,
                                            ),
                                          ],
                                        };
                                      });
                                    }}
                                    value={fieldValue}
                                  />
                                </FormGroup>
                              )}
                            </div>
                          )}
                        </Draggable>
                      </Col>
                    );
                  });
                })}
                {isElementOnDrag ? (
                  <Col className="pl-4" xs="8" style={{ height: '102px' }}></Col>
                ) : null}
              </Row>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default FullyEditableFormSection;
